import React, { useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import analyticsService from '../../services/analyticsService';
import LoadingSpinner from '../common/LoadingSpinner';

/** Fallback country centroids [lat, lng] when API returns null. Free, no external service. */
const COUNTRY_CENTROIDS = {
  Pakistan: [30.3753, 69.3451],
  'United States': [37.0902, -95.7129],
  'United Kingdom': [55.3781, -3.4360],
  India: [20.5937, 78.9629],
  Canada: [56.1304, -106.3468],
  Australia: [-25.2744, 133.7751],
  Germany: [51.1657, 10.4515],
  France: [46.2276, 2.2137],
  'Saudi Arabia': [23.8859, 45.0792],
  UAE: [23.4241, 53.8478],
  China: [35.8617, 104.1954],
  Turkey: [38.9637, 35.2433],
  Malaysia: [4.2105, 101.9758],
  Indonesia: [-0.7893, 113.9213],
  Egypt: [26.8206, 30.8025],
  Nigeria: [9.0820, 8.6753],
  'South Africa': [-30.5595, 22.9375],
  Brazil: [-14.2350, -51.9253],
  Mexico: [23.6345, -102.5528],
  Spain: [40.4637, -3.7492],
  Italy: [41.8719, 12.5674],
  Netherlands: [52.1326, 5.2913],
  Belgium: [50.5039, 4.4699],
  Switzerland: [46.8182, 8.2275],
  Austria: [47.5162, 14.5501],
  Poland: [51.9194, 19.1451],
  Russia: [61.5240, 105.3188],
  Japan: [36.2048, 138.2529],
  'South Korea': [35.9078, 127.7669],
  Singapore: [1.3521, 103.8198],
  Bangladesh: [23.6850, 90.3563],
  Iran: [32.4279, 53.6880],
  Iraq: [33.2232, 43.6793],
  Kuwait: [29.3117, 47.4818],
  Qatar: [25.2854, 51.5310],
  Oman: [21.4735, 55.9754],
  Bahrain: [26.0667, 50.5577],
  Afghanistan: [33.9391, 67.7100],
  'Sri Lanka': [7.8731, 80.7718],
  Nepal: [28.3949, 84.1240],
  Thailand: [15.8700, 100.9925],
  Philippines: [12.8797, 121.7740],
  Vietnam: [14.0583, 108.2772],
};

/** Fits map bounds to include all points when map and points are ready. */
function MapFitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 4);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p[0], p[1]]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
  }, [map, points]);
  return null;
}

/**
 * Normalize analytics API payload after axios ApiResponse unwrap and legacy shapes.
 */
function extractAnalyticsPayload(res) {
  if (!res || typeof res !== 'object') return null;
  if (res.success === true && res.data != null && typeof res.data === 'object') {
    const inner = res.data;
    if (inner.status === 'success' && inner.data != null) return inner.data;
    if (inner.total_visitors != null || inner.daily_visitors != null || inner.most_visited_pages != null) {
      return inner;
    }
  }
  if (res.status === 'success' && res.data != null) return res.data;
  if (res.total_visitors != null || res.daily_visitors != null || res.most_visited_pages != null) {
    return res;
  }
  return null;
}

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');
  const [realTimeData, setRealTimeData] = useState(null);
  const worldMapRef = useRef(null);

  useEffect(() => {
    loadAnalyticsData();
    loadRealTimeData();
    const interval = setInterval(() => loadRealTimeData(), 30000);
    return () => clearInterval(interval);
  }, [timePeriod]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getAnalyticsData(timePeriod);
      const payload = extractAnalyticsPayload(response);
      if (payload) {
        setAnalyticsData(payload);
      } else {
        setError(
          response && typeof response === 'object' && response.message
            ? response.message
            : 'Failed to load analytics'
        );
      }
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load analytics');
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRealTimeData = async () => {
    try {
      const response = await analyticsService.getRealTimeAnalytics();
      let payload = null;
      if (response && typeof response === 'object') {
        if (response.success === true && response.data != null) {
          const inner = response.data;
          payload = inner.status === 'success' && inner.data != null ? inner.data : inner;
        } else if (response.status === 'success' && response.data != null) {
          payload = response.data;
        } else if (response.active_visitors != null || response.current_pages != null) {
          payload = response;
        }
      }
      if (payload && (payload.active_visitors != null || payload.current_pages != null)) {
        setRealTimeData(payload);
      }
    } catch (err) {
      console.error('Error loading real-time analytics:', err);
      setRealTimeData(null);
    }
  };

  const refreshData = () => {
    loadAnalyticsData();
    loadRealTimeData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !analyticsData) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 text-4xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Analytics</h3>
        <p className="text-gray-500 mb-4">{error || 'No analytics data available.'}</p>
        <button
          onClick={refreshData}
          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
        <p className="text-gray-500">Unable to load analytics data.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'geographic', label: 'Geographic', icon: '🌍' },
    { id: 'devices', label: 'Devices', icon: '📱' },
    { id: 'content', label: 'Content', icon: '📝' },
    { id: 'realtime', label: 'Real-time', icon: '⚡' }
  ];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Visitors</p>
              <p className="text-3xl font-bold">{analyticsData.total_visitors?.toLocaleString() || 0}</p>
            </div>
            <div className="text-blue-200">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Unique Visitors</p>
              <p className="text-3xl font-bold">{analyticsData.unique_visitors?.toLocaleString() || 0}</p>
            </div>
            <div className="text-green-200">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM6.175 15.5a9.003 9.003 0 0017.65 0H6.175z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Page Views</p>
              <p className="text-3xl font-bold">{analyticsData.page_views?.toLocaleString() || 0}</p>
            </div>
            <div className="text-purple-200">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Bounce Rate</p>
              <p className="text-3xl font-bold">{analyticsData.bounce_rate || 0}%</p>
            </div>
            <div className="text-orange-200">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visitor Trends Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Visitor Trends</h3>
          <div className="h-64">
            <Line
              data={{
                labels: analyticsData.daily_visitors?.map(d => d.date) || [],
                datasets: [{
                  label: 'Visitors',
                  data: analyticsData.daily_visitors?.map(d => d.count) || [],
                  borderColor: 'rgb(59, 130, 246)',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  tension: 0.4
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Device Breakdown Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Breakdown</h3>
          <div className="h-64">
            {Object.keys(analyticsData.device_breakdown || {}).length > 0 ? (
            <Doughnut
              data={{
                labels: Object.keys(analyticsData.device_breakdown || {}),
                datasets: [{
                  data: Object.values(analyticsData.device_breakdown || {}).map(d => (d && d.count) ?? 0),
                  backgroundColor: [
                    'rgb(59, 130, 246)',
                    'rgb(16, 185, 129)',
                    'rgb(245, 158, 11)'
                  ]
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                }
              }}
            />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">No device data for this period.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderGeographicTab = () => {
    const worldData = Array.isArray(analyticsData.world_map_data) ? analyticsData.world_map_data : [];
    const pointsWithCoords = worldData
      .map((d) => {
        let lat = d.latitude != null ? Number(d.latitude) : null;
        let lng = d.longitude != null ? Number(d.longitude) : null;
        if ((lat == null || lng == null) && d.country) {
          const fallback = COUNTRY_CENTROIDS[d.country];
          if (fallback) {
            lat = fallback[0];
            lng = fallback[1];
          }
        }
        if (lat == null || lng == null) return null;
        return { ...d, lat, lng };
      })
      .filter(Boolean);
    const maxVisitors = Math.max(1, ...pointsWithCoords.map((d) => d.visitors ?? 0));
    const boundsPoints = pointsWithCoords.map((d) => [d.lat, d.lng]);

    return (
    <div className="space-y-6">
      {/* Interactive world map - OpenStreetMap (free, no API key) */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">World Map – Visitor Distribution</h3>
        <p className="text-sm text-gray-500 mb-3">Click a circle for details. Tiles: OpenStreetMap (free).</p>
        <div className="h-96 rounded-lg overflow-hidden border border-gray-200">
          {pointsWithCoords.length > 0 ? (
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapFitBounds points={boundsPoints} />
              {pointsWithCoords.map((d, idx) => {
                const radius = Math.min(40, Math.max(8, 8 + 24 * Math.sqrt((d.visitors ?? 0) / maxVisitors)));
                return (
                  <CircleMarker
                    key={`${d.country}-${idx}`}
                    center={[d.lat, d.lng]}
                    radius={radius}
                    pathOptions={{
                      fillColor: '#2563eb',
                      color: '#1d4ed8',
                      weight: 1.5,
                      fillOpacity: 0.6,
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold text-gray-900">{d.country ?? '—'}</div>
                        <div>Visitors: {(d.visitors ?? 0).toLocaleString()}</div>
                        <div>Unique: {(d.unique_visitors ?? 0).toLocaleString()}</div>
                        <div>Page views: {(d.page_views ?? 0).toLocaleString()}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center bg-gray-50 text-gray-500 text-sm">
              No geographic data for this period. Visitor locations will appear here when available.
            </div>
          )}
        </div>
      </div>

      {/* Top Countries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h3>
          <div className="space-y-3">
            {Array.isArray(analyticsData.top_countries) && analyticsData.top_countries.length > 0 ? (
              analyticsData.top_countries.slice(0, 10).map((country, index) => (
                <div key={`${country.country}-${index}`} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">{country.country ?? '—'}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{(country.visitors ?? 0).toLocaleString()}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 py-4">No country data for this period.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Cities</h3>
          <div className="space-y-3">
            {Array.isArray(analyticsData.top_cities) && analyticsData.top_cities.length > 0 ? (
              analyticsData.top_cities.slice(0, 10).map((city, index) => (
                <div key={`${city.city}-${index}`} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">{city.city ?? '—'}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{(city.visitors ?? 0).toLocaleString()}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 py-4">No city data for this period.</p>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderDevicesTab = () => {
    const browserData = analyticsData.browser_breakdown || {};
    const osData = analyticsData.os_breakdown || {};
    const deviceData = analyticsData.device_breakdown || {};
    const browserLabels = Object.keys(browserData).slice(0, 5);
    const osLabels = Object.keys(osData).slice(0, 5);
    return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Browser Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Browser Breakdown</h3>
          <div className="h-64">
            {browserLabels.length > 0 ? (
            <Bar
              data={{
                labels: browserLabels,
                datasets: [{
                  label: 'Visitors',
                  data: browserLabels.map(k => (browserData[k] && browserData[k].count) ?? 0),
                  backgroundColor: 'rgb(59, 130, 246)'
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }}
            />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">No browser data for this period.</div>
            )}
          </div>
        </div>

        {/* OS Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Operating System</h3>
          <div className="h-64">
            {osLabels.length > 0 ? (
            <Bar
              data={{
                labels: osLabels,
                datasets: [{
                  label: 'Visitors',
                  data: osLabels.map(k => (osData[k] && osData[k].count) ?? 0),
                  backgroundColor: 'rgb(139, 92, 246)'
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }}
            />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">No OS data for this period.</div>
            )}
          </div>
        </div>
      </div>

      {/* Device Details */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.keys(deviceData).length > 0 ? (
            Object.entries(deviceData).map(([device, data]) => (
              <div key={device} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{data?.count ?? 0}</div>
                <div className="text-sm text-gray-600 capitalize">{device}</div>
                <div className="text-xs text-gray-500">{data?.percentage ?? 0}%</div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-sm text-gray-500">No device data for this period.</div>
          )}
        </div>
      </div>
    </div>
  );
  };

  const renderContentTab = () => (
    <div className="space-y-6">
      {/* Most Visited Pages */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Visited Pages</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Views</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Duration</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(analyticsData.most_visited_pages) && analyticsData.most_visited_pages.length > 0 ? (
                analyticsData.most_visited_pages.slice(0, 10).map((page, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{page.page_title || 'Unknown'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{(page.views ?? 0).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{(page.unique_views ?? 0).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round((page.avg_duration || 0) / 60)}m</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">No page data for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Content Performance */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Performance</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Likes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(analyticsData.top_content) && analyticsData.top_content.length > 0 ? (
                analyticsData.top_content.slice(0, 10).map((content, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{content.title || 'Unknown'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{content.content_type ?? '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{(content.views ?? 0).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{(content.likes ?? 0).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{(content.comments ?? 0).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{content.engagement_rate ?? 0}%</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">No content data for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderRealtimeTab = () => (
    <div className="space-y-6">
      {/* Real-time Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Active Visitors</p>
              <p className="text-3xl font-bold">{realTimeData?.active_visitors || analyticsData.active_visitors || 0}</p>
            </div>
            <div className="text-green-200">
              <div className="w-3 h-3 bg-green-300 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Recent Page Views</p>
              <p className="text-3xl font-bold">{realTimeData?.recent_page_views || 0}</p>
            </div>
            <div className="text-blue-200">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Countries</p>
              <p className="text-3xl font-bold">{analyticsData.top_countries?.length || 0}</p>
            </div>
            <div className="text-purple-200">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Current Pages */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Currently Being Viewed</h3>
        <div className="space-y-3">
          {Array.isArray(realTimeData?.current_pages) && realTimeData.current_pages.length > 0 ? (
            realTimeData.current_pages.slice(0, 10).map((page, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-900">{page.page_title || 'Unknown Page'}</div>
                  <div className="text-xs text-gray-500">{page.current_page}</div>
                </div>
                <div className="text-sm text-gray-600">{page.viewers ?? 0} viewers</div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">👥</div>
              <p>No active visitors at the moment</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600 mt-1">Comprehensive visitor analytics and insights</p>
        </div>
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(parseInt(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={refreshData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'geographic' && renderGeographicTab()}
        {activeTab === 'devices' && renderDevicesTab()}
        {activeTab === 'content' && renderContentTab()}
        {activeTab === 'realtime' && renderRealtimeTab()}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
